import { useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { useTimer } from "@/context/TimerContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PriorityBadge, TASK_STATUS, budgetColor } from "@/mockups/ui";
import ProjectFinance from "./ProjectFinance";
import BudgetTab from "./BudgetTab";
import ProjectCierre from "./ProjectCierre";
import ProjectMembers from "./ProjectMembers";
import ProjectExternalPayments from "./ProjectExternalPayments";
import PaymentSchedule from "./PaymentSchedule";
import { toast } from "sonner";
import { cn, fmtHM } from "@/lib/utils";
import {
  ChevronLeft, Plus, Play, MoreHorizontal, Flag, Clock, Target, TrendingUp, CheckSquare, Trash2, Pencil,
  ChevronDown, ChevronRight, ListChecks, Lock, LockOpen, Banknote,
} from "lucide-react";

const STATUS_ORDER = ["pending", "in_progress", "review", "done"];
const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("");
const EMPTY_TASK = { title: "", description: "", assignee_id: "none", milestone_id: "none", priority: "medium", estimated_hours: 0, status: "pending", due_date: "" };

function TaskDialog({ open, onOpenChange, projectId, milestones, team, initial, onSaved }) {
  const [form, setForm] = useState(initial
    ? { ...EMPTY_TASK, ...initial, assignee_id: initial.assignee_id || "none", milestone_id: initial.milestone_id || "none", due_date: initial.due_date || "" }
    : EMPTY_TASK);
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title, description: form.description, priority: form.priority, status: form.status,
        estimated_hours: Number(form.estimated_hours) || 0,
        assignee_id: form.assignee_id === "none" ? null : form.assignee_id,
        milestone_id: form.milestone_id === "none" ? null : form.milestone_id,
        due_date: form.due_date || null,
      };
      if (editing) await api.patch(`/tasks/${initial.id}`, payload);
      else await api.post("/tasks", { ...payload, project_id: projectId });
      toast.success(editing ? "Tarea actualizada" : "Tarea creada");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{editing ? "Editar tarea" : "Nueva tarea"}</DialogTitle><DialogDescription className="sr-only">Formulario de tarea</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2 space-y-1.5"><Label>Título *</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="task-title-input" /></div>
          <div className="space-y-1.5">
            <Label>Asignado a</Label>
            <Select value={form.assignee_id} onValueChange={(v) => set("assignee_id", v)}>
              <SelectTrigger data-testid="task-assignee-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin asignar</SelectItem>
                {(team || []).map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Hito</Label>
            <Select value={form.milestone_id} onValueChange={(v) => set("milestone_id", v)}>
              <SelectTrigger data-testid="task-milestone-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin hito</SelectItem>
                {(milestones || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Prioridad</Label>
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Baja</SelectItem>
                <SelectItem value="medium">Media</SelectItem>
                <SelectItem value="high">Alta</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Estado</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_ORDER.map((k) => <SelectItem key={k} value={k}>{TASK_STATUS[k].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Horas estimadas</Label><Input type="number" value={form.estimated_hours} onChange={(e) => set("estimated_hours", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Fecha límite</Label><Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} /></div>
          <div className="col-span-2 space-y-1.5"><Label>Descripción</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="resize-none" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="task-save-btn">{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TaskRow({ t, canEdit, canDelete, onMove, onEdit, onDelete, onTimer }) {
  const st = TASK_STATUS[t.status] || TASK_STATUS.pending;
  return (
    <div className="group flex items-center gap-3 px-3 py-2.5 hover:bg-muted/40 transition-colors duration-150" data-testid={`task-row-${t.id}`}>
      <span className={cn("h-2.5 w-2.5 rounded-full shrink-0", st.dot)} title={st.label} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-snug truncate">{t.title}</p>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          <Badge variant="outline" className={cn("border-transparent text-[10px] px-1.5 py-0", st.soft)}>{st.label}</Badge>
          <PriorityBadge priority={t.priority} />
        </div>
      </div>
      {t.assignee_name ? (
        <div className="flex items-center gap-1.5 shrink-0">
          <Avatar className="h-6 w-6"><AvatarImage src={t.assignee_picture} /><AvatarFallback className="text-[9px] bg-muted">{initials(t.assignee_name)}</AvatarFallback></Avatar>
          <span className="text-xs text-muted-foreground hidden sm:inline">{t.assignee_name.split(" ")[0]}</span>
        </div>
      ) : <span className="text-[11px] text-muted-foreground/60 shrink-0 hidden sm:inline">Sin asignar</span>}
      <div className="flex items-center gap-3 shrink-0 tabular-nums">
        <span className="text-xs inline-flex items-center gap-1 text-timer font-medium" title="Horas consumidas"><Clock className="h-3 w-3" /> {fmtHM(t.hours_spent)}</span>
        {t.estimated_hours > 0 && <span className="text-xs items-center gap-1 text-muted-foreground/70 hidden sm:inline-flex" title="Horas estimadas"><Target className="h-3 w-3" /> {fmtHM(t.estimated_hours)}</span>}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="opacity-40 group-hover:opacity-100 transition-opacity duration-200 text-muted-foreground shrink-0" data-testid={`task-menu-${t.id}`}><MoreHorizontal className="h-4 w-4" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onTimer(t)} data-testid={`task-timer-${t.id}`}><Play className="h-4 w-4 mr-2" /> Iniciar timer</DropdownMenuItem>
          {canEdit && <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">Mover a</DropdownMenuLabel>
            {STATUS_ORDER.map((k) => (
              <DropdownMenuItem key={k} onClick={() => onMove(t.id, k)} data-testid={`move-${t.id}-${k}`}>
                <span className={cn("h-2 w-2 rounded-full mr-2", TASK_STATUS[k].dot)} /> {TASK_STATUS[k].label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onEdit(t)}><Pencil className="h-4 w-4 mr-2" /> Editar</DropdownMenuItem>
          </>}
          {canDelete && <DropdownMenuItem onClick={() => onDelete(t.id)} className="text-destructive focus:text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Eliminar</DropdownMenuItem>}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function MilestoneGroup({ m, tasks, canEditTasks, canDeleteTasks, canEditProject, defaultOpen, onAddTask, onMove, onEdit, onDelete, onTimer, onDeleteMilestone, onRenameMilestone, onToggleClose }) {
  const [open, setOpen] = useState(defaultOpen);
  const done = tasks.filter((t) => t.status === "done").length;
  const p2 = tasks.length > 0 ? (done / tasks.length) * 100 : 0;
  const assigned = tasks.reduce((a, t) => a + (Number(t.estimated_hours) || 0), 0);
  const spent = m.id ? (m.hours_spent || 0) : tasks.reduce((a, t) => a + (Number(t.hours_spent) || 0), 0);
  const isReal = !!m.id;
  const closed = !!m.closed;

  return (
    <Card className={cn("border-border shadow-none overflow-hidden", closed && "opacity-60 border-dashed")} data-testid={`milestone-group-${m.id || "none"}`}>
      <div className={cn("flex items-center gap-3 px-3 py-3 border-b border-border", closed ? "bg-muted/60" : "bg-navy/[0.03]")}>
        <button onClick={() => setOpen((o) => !o)} className="text-muted-foreground shrink-0" data-testid={`milestone-toggle-${m.id || "none"}`}>
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <div className={cn("h-9 w-9 rounded-md flex items-center justify-center shrink-0", isReal ? (closed ? "bg-muted text-muted-foreground" : (tasks.length > 0 && done === tasks.length ? "bg-primary/12 text-primary" : "bg-navy/8 text-navy")) : "bg-muted text-muted-foreground")}>
          {isReal ? <Flag className="h-4 w-4" /> : <ListChecks className="h-4 w-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm truncate">{m.name}</span>
            {closed && <Badge variant="secondary" className="h-5 px-1.5 text-[10px] gap-1 shrink-0" data-testid={`milestone-closed-badge-${m.id}`}><Lock className="h-2.5 w-2.5" /> Cerrado</Badge>}
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground flex-wrap">
            <span>{done}/{tasks.length} tareas</span>
            <span className="inline-flex items-center gap-1 text-timer font-medium"><Clock className="h-3 w-3" /> {fmtHM(spent)} consumidas</span>
            {assigned > 0 && <span className="inline-flex items-center gap-1"><Target className="h-3 w-3" /> {fmtHM(assigned)} estimadas</span>}
          </div>
        </div>
        <div className="w-24 sm:w-32 h-1.5 rounded-full bg-muted overflow-hidden shrink-0 hidden sm:block"><div className="h-full bg-primary rounded-full transition-all" style={{ width: `${p2}%` }} /></div>
        {canEditTasks && <Button variant="ghost" size="sm" className="h-8 gap-1 text-muted-foreground shrink-0" onClick={() => onAddTask(m.id)} data-testid={`milestone-add-task-${m.id || "none"}`}><Plus className="h-4 w-4" /> <span className="hidden sm:inline">Tarea</span></Button>}
        {isReal && canEditProject && (
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-navy shrink-0" onClick={() => onRenameMilestone(m)} data-testid={`milestone-edit-${m.id}`} title="Editar nombre"><Pencil className="h-4 w-4" /></Button>
        )}
        {isReal && canEditProject && (
          <Button variant="ghost" size="icon" className={cn("h-8 w-8 shrink-0", closed ? "text-primary hover:text-primary" : "text-muted-foreground hover:text-navy")} onClick={() => onToggleClose(m)} data-testid={`milestone-close-${m.id}`} title={closed ? "Reabrir hito" : "Cerrar hito (bloquea imputar horas)"}>{closed ? <LockOpen className="h-4 w-4" /> : <Lock className="h-4 w-4" />}</Button>
        )}
        {isReal && canEditProject && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0" onClick={() => onDeleteMilestone(m.id)} data-testid={`milestone-del-${m.id}`} title="Eliminar hito"><Trash2 className="h-4 w-4" /></Button>}
      </div>
      {open && (
        <div className="divide-y divide-border/60">
          {tasks.length === 0
            ? <p className="text-xs text-muted-foreground/60 text-center py-6">Sin tareas en este hito.</p>
            : tasks.map((t) => (
              <TaskRow key={t.id} t={t} canEdit={canEditTasks} canDelete={canDeleteTasks} onMove={onMove} onEdit={onEdit} onDelete={onDelete} onTimer={onTimer} />
            ))}
        </div>
      )}
    </Card>
  );
}

export default function ProyectoDetalle() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(tabParam || "milestones");
  const { can, user } = useAuth();
  const { start } = useTimer();
  const navigate = useNavigate();
  const { data: project, refetch } = useFetch(`/projects/${id}`);
  const { data: team } = useFetch("/team");
  const [taskDialog, setTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [newMilestone, setNewMilestone] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const canEditTasks = can("tasks", "edit");
  const canDeleteTasks = can("tasks", "delete");

  const move = async (taskId, status) => {
    try { await api.patch(`/tasks/${taskId}`, { status }); refetch(); }
    catch (e) { toast.error("No se pudo mover la tarea"); }
  };
  const delTask = async (taskId) => {
    if (!window.confirm("¿Eliminar esta tarea?")) return;
    try { await api.delete(`/tasks/${taskId}`); toast.success("Tarea eliminada"); refetch(); }
    catch (e) { toast.error("Error"); }
  };
  const startTaskTimer = async (t) => {
    try { await start({ project_id: id, task_id: t.id, milestone_id: t.milestone_id, description: t.title }); }
    catch (e) { toast.error("Error al iniciar timer"); }
  };
  const openNewTask = (milestoneId) => { setEditingTask({ ...EMPTY_TASK, milestone_id: milestoneId || "none" }); setTaskDialog(true); };
  const addMilestone = async () => {
    if (!newMilestone.trim()) return;
    try {
      await api.post("/milestones", { project_id: id, name: newMilestone, order: (project.milestones || []).length });
      setNewMilestone(""); toast.success("Hito añadido"); refetch();
    } catch (e) { toast.error("Error"); }
  };
  const delMilestone = async (mid) => {
    if (!window.confirm("¿Eliminar este hito? Las tareas quedarán sin hito.")) return;
    try { await api.delete(`/milestones/${mid}`); refetch(); } catch (e) { toast.error("Error"); }
  };
  const openRename = (m) => { setRenameTarget(m); setRenameValue(m.name || ""); };
  const saveRename = async () => {
    const nm = renameValue.trim();
    if (!nm) { toast.error("El nombre no puede estar vacío"); return; }
    try {
      await api.patch(`/milestones/${renameTarget.id}`, { name: nm });
      setRenameTarget(null); toast.success("Hito actualizado"); refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const toggleCloseMilestone = async (m) => {
    const closing = !m.closed;
    if (closing && !window.confirm(`¿Cerrar el hito «${m.name}»? No desaparece, pero nadie podrá imputar horas hasta reabrirlo.`)) return;
    try {
      await api.patch(`/milestones/${m.id}`, { closed: closing });
      toast.success(closing ? "Hito cerrado" : "Hito reabierto"); refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (!project) {
    return <div className="flex justify-center py-20"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  }

  const tasks = project.tasks || [];
  const milestones = project.milestones || [];
  const pct = project.budget_hours > 0 ? Math.round((project.hours_spent / project.budget_hours) * 100) : 0;
  const remaining = Math.max(0, (project.budget_hours || 0) - (project.hours_spent || 0));
  const noMilestoneTasks = tasks.filter((t) => !t.milestone_id);
  const STATS = [
    { label: "Presupuesto", value: fmtHM(project.budget_hours), icon: Target, accent: "text-muted-foreground bg-muted" },
    { label: "Consumidas", value: fmtHM(project.hours_spent), icon: Clock, accent: "text-chart-4 bg-chart-4/12" },
    { label: "Restantes", value: fmtHM(remaining), icon: TrendingUp, accent: "text-primary bg-primary/12" },
    { label: "Tareas", value: `${project.tasks_done}/${project.tasks_total}`, icon: CheckSquare, accent: "text-gold bg-gold/15" },
  ];
  const PROJ_STATUS_LABEL = { active: "Activo", on_hold: "En pausa", done: "Finalizado", archived: "Archivado" };

  const canFinance = project.my_finance_access && project.my_finance_access !== "none";
  const canBudget = project.my_budget_access && project.my_budget_access !== "none";
  const canCierre = project.my_cierre_access && project.my_cierre_access !== "none";
  const canExtPay = (can("external_payments", "view") || (project.my_extpay_access && project.my_extpay_access !== "none")) && project.external_payments_enabled;
  const canManageExtPay = user?.role === "Administrador" || project.my_is_lead;
  const canDelete = user?.role === "Administrador" || project.my_is_lead;
  const canManageMilestones = can("projects", "edit") || project.my_is_lead;
  const isClosed = project.status === "done";

  const deleteProject = async () => {
    if (!window.confirm(`¿Eliminar el proyecto "${project.name}"? Se borrarán sus tareas, hitos, presupuesto y finanzas. Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Proyecto eliminado");
      navigate("/proyectos");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar el proyecto"); }
  };

  const toggleCloseProject = async () => {
    const closing = !isClosed;
    if (closing && !window.confirm(`¿Cerrar (finalizar) el proyecto "${project.name}"?`)) return;
    try {
      await api.post(`/projects/${id}/close`, { closed: closing });
      toast.success(closing ? "Proyecto cerrado" : "Proyecto reabierto");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cambiar el estado"); }
  };

  const toggleExtPay = async () => {
    const enable = !project.external_payments_enabled;
    if (!enable && !window.confirm("¿Quitar la pestaña de Pagos externos de este proyecto? No se borra ningún pago; solo deja de mostrarse aquí.")) return;
    try {
      await api.patch(`/projects/${id}/external-payments-option`, { enabled: enable });
      toast.success(enable ? "Pagos externos añadido al proyecto" : "Pagos externos quitado del proyecto");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cambiar la opción"); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <Link to="/proyectos" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200" data-testid="back-to-projects">
        <ChevronLeft className="h-4 w-4" /> Proyectos
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-5">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-heading font-bold text-2xl sm:text-[28px] tracking-tight">{project.name}</h2>
            <Badge variant="outline" className="border-transparent bg-primary/12 text-primary font-medium">{PROJ_STATUS_LABEL[project.status]}</Badge>
            {project.reference && <span className="text-xs text-muted-foreground font-mono bg-muted rounded px-1.5 py-0.5">{project.reference}</span>}
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {project.company_name || "Sin cliente"}{project.manager_name && <> · Responsable <span className="text-foreground font-medium">{project.manager_name}</span></>}
            {project.due_date && <> · Vence {new Date(project.due_date).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}</>}
          </p>
        </div>
        <div className="flex gap-2">
          {can("timesheets", "create") && <Button variant="outline" size="sm" className="gap-1.5 border-timer/40 text-timer hover:bg-timer/10 hover:text-timer" onClick={() => start({ project_id: id, description: project.name })} data-testid="project-start-timer"><Play className="h-4 w-4" /> Iniciar timer</Button>}
          {can("tasks", "create") && <Button size="sm" className="gap-1.5" onClick={() => openNewTask(null)} data-testid="project-new-task"><Plus className="h-4 w-4" /> Nueva tarea</Button>}
          {canManageExtPay && !project.external_payments_enabled && <Button variant="outline" size="sm" className="gap-1.5" onClick={toggleExtPay} data-testid="project-add-extpay-btn"><Plus className="h-4 w-4" /> Añadir Pagos externos</Button>}
          {canManageExtPay && project.external_payments_enabled && <Button variant="outline" size="sm" className="gap-1.5 text-muted-foreground" onClick={toggleExtPay} data-testid="project-remove-extpay-btn"><Banknote className="h-4 w-4" /> Quitar Pagos externos</Button>}
          {canDelete && <Button variant="outline" size="sm" className="gap-1.5" onClick={toggleCloseProject} data-testid="project-close-btn">{isClosed ? <><LockOpen className="h-4 w-4" /> Reabrir</> : <><Lock className="h-4 w-4" /> Cerrar</>}</Button>}
          {canDelete && <Button variant="outline" size="sm" className="gap-1.5 text-destructive hover:text-destructive" onClick={deleteProject} data-testid="project-delete-btn"><Trash2 className="h-4 w-4" /> Eliminar</Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STATS.map((s, i) => (
          <Card key={i} className="border-border shadow-none p-4 flex items-center gap-3">
            <div className={cn("h-10 w-10 rounded-md flex items-center justify-center", s.accent)}><s.icon className="h-5 w-5" /></div>
            <div><div className="font-heading font-bold text-xl tabular-nums leading-none">{s.value}</div><div className="text-xs text-muted-foreground mt-1">{s.label}</div></div>
          </Card>
        ))}
      </div>

      {project.budget_hours > 0 && (
        <Card className="border-border shadow-none p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Consumo del presupuesto de horas</span>
            <span className="text-sm tabular-nums font-semibold">{pct}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-muted overflow-hidden"><div className={cn("h-full rounded-full", budgetColor(pct))} style={{ width: `${Math.min(pct, 100)}%` }} /></div>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="project-tabs">
          <TabsTrigger value="milestones">Hitos y tareas</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">Equipo</TabsTrigger>
          {canFinance && <TabsTrigger value="finance" data-testid="tab-finance">Finanzas</TabsTrigger>}
          {canBudget && <TabsTrigger value="budget" data-testid="tab-budget">Presupuesto</TabsTrigger>}
          {canBudget && <TabsTrigger value="schedule" data-testid="tab-schedule">Cronograma de pago</TabsTrigger>}
          {canCierre && <TabsTrigger value="cierre" data-testid="tab-cierre">Cierre</TabsTrigger>}
          {canExtPay && <TabsTrigger value="extpay" data-testid="tab-extpay">Pagos externos</TabsTrigger>}
        </TabsList>

        <TabsContent value="milestones" className="mt-5 space-y-3">
          {can("projects", "edit") && (
            <div className="flex gap-2">
              <Input placeholder="Nombre del hito…" value={newMilestone} onChange={(e) => setNewMilestone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addMilestone()} data-testid="milestone-input" />
              <Button onClick={addMilestone} className="gap-1.5" data-testid="milestone-add-btn"><Plus className="h-4 w-4" /> Añadir hito</Button>
            </div>
          )}

          {milestones.length === 0 && noMilestoneTasks.length === 0 && tasks.length === 0 ? (
            <Card className="border-border border-dashed shadow-none py-12 text-center">
              <p className="text-sm text-muted-foreground">Sin hitos ni tareas todavía.</p>
              {can("tasks", "create") && <Button size="sm" className="gap-1.5 mt-3" onClick={() => openNewTask(null)} data-testid="milestones-empty-add"><Plus className="h-4 w-4" /> Crear la primera tarea</Button>}
            </Card>
          ) : (
            <div className="space-y-3">
              {milestones.map((m, i) => (
                <MilestoneGroup key={m.id} m={m} tasks={tasks.filter((t) => t.milestone_id === m.id)}
                  canEditTasks={canEditTasks} canDeleteTasks={canDeleteTasks} canEditProject={canManageMilestones} defaultOpen={i < 3}
                  onAddTask={openNewTask} onMove={move} onEdit={(tk) => { setEditingTask(tk); setTaskDialog(true); }}
                  onDelete={delTask} onTimer={startTaskTimer} onDeleteMilestone={delMilestone}
                  onRenameMilestone={openRename} onToggleClose={toggleCloseMilestone} />
              ))}
              {(noMilestoneTasks.length > 0 || milestones.length === 0) && (
                <MilestoneGroup m={{ id: null, name: "Sin hito", hours_spent: 0 }} tasks={noMilestoneTasks}
                  canEditTasks={canEditTasks} canDeleteTasks={canDeleteTasks} canEditProject={can("projects", "edit")} defaultOpen
                  onAddTask={openNewTask} onMove={move} onEdit={(tk) => { setEditingTask(tk); setTaskDialog(true); }}
                  onDelete={delTask} onTimer={startTaskTimer} onDeleteMilestone={delMilestone}
                  onRenameMilestone={openRename} onToggleClose={toggleCloseMilestone} />
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="team" className="mt-5">
          <ProjectMembers projectId={id} onChanged={refetch} />
        </TabsContent>

        {canFinance && (
          <TabsContent value="finance" className="mt-5">
            <ProjectFinance projectId={id} ingresosAccess={project.my_ingresos_access} gastosAccess={project.my_gastos_access} onOpenSchedule={canBudget ? () => setActiveTab("schedule") : undefined} />
          </TabsContent>
        )}

        {canBudget && (
          <TabsContent value="budget" className="mt-5">
            <BudgetTab projectId={id} mode={project.my_budget_access} />
          </TabsContent>
        )}

        {canBudget && (
          <TabsContent value="schedule" className="mt-5">
            <PaymentSchedule projectId={id} access={project.my_budget_access} />
          </TabsContent>
        )}

        {canCierre && (
          <TabsContent value="cierre" className="mt-5">
            <ProjectCierre projectId={id} />
          </TabsContent>
        )}

        {canExtPay && (
          <TabsContent value="extpay" className="mt-5">
            <ProjectExternalPayments projectId={id} />
          </TabsContent>
        )}
      </Tabs>

      {taskDialog && <TaskDialog open={taskDialog} onOpenChange={setTaskDialog} projectId={id} milestones={project.milestones} team={team} initial={editingTask} onSaved={refetch} />}

      <Dialog open={!!renameTarget} onOpenChange={(o) => { if (!o) setRenameTarget(null); }}>
        <DialogContent data-testid="milestone-rename-dialog">
          <DialogHeader>
            <DialogTitle>Editar nombre del hito</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Nombre</Label>
            <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveRename()} autoFocus data-testid="milestone-rename-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)} data-testid="milestone-rename-cancel">Cancelar</Button>
            <Button onClick={saveRename} data-testid="milestone-rename-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
